/**
 * ExplanationQuiz Student Renderer
 * 서술형 문제 - Student 모드 UI
 */

import { useMemo, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { cn } from '../../../lib/utils'
import KatexRenderer from '../../../utils/KatexRenderer'
import { formatProblem } from '../../../utils/quiz-parser'
import { SubmitButton, QuizHeader, QuizResultFeedback, QuizResultToggle, type QuizResultToggleValue, QuizSolveDescription } from '../shared'
import type { BaseQuizData, QuizSolveResult } from '../../../types/legacyQuiz'
import { useExplanationQuiz } from '../hooks'

interface ExplanationStudentProps {
  quizId: string | number
  quiz: BaseQuizData
  onSolve?: (quizId: string | number, answers: string[], vars: Record<string, unknown>, elapsedTime: number) => void
  solveResult?: QuizSolveResult | null
  isViewSolved?: boolean
  initialAnswers?: string[]
  submitLabel?: string
  hideSubmitButton?: boolean
  onAnswerChange?: (answers: string[]) => void
  disableSound?: boolean
  difficultyLevel?: number | null
  /** G3 — true면 풀이 후 해설(QuizSolveDescription)을 숨긴다(정오답 피드백은 유지). 기본 false=현행 노출. */
  hideExplanation?: boolean
  /** true면 결과 토글은 유지하되 풀이/해설 UI만 숨긴다. */
  hideSolveDescription?: boolean
  hideResultFeedback?: boolean
  /** 답안 입력 없이 외부 액션으로 모범답안만 확인하는 복습 모드. */
  answerRevealOnly?: boolean
}

export function ExplanationStudent({
  quizId,
  quiz,
  onSolve,
  solveResult,
  isViewSolved = false,
  initialAnswers: initialAnswersProp,
  submitLabel,
  hideSubmitButton = false,
  onAnswerChange,
  disableSound = false,
  difficultyLevel,
  hideExplanation = false,
  hideSolveDescription = false,
  hideResultFeedback = false,
  answerRevealOnly = false,
}: ExplanationStudentProps) {
  const { t } = useTranslation('quiz')

  const {
    answer,
    setAnswer,
    isSolved,
    canSubmit,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    handleSubmit,
  } = useExplanationQuiz({
    quizId,
    quiz,
    onSolve,
    solveResult,
    isViewSolved,
    initialAnswers: initialAnswersProp,
    onAnswerChange,
    showCorrectAnswersInitially: false,
  })

  const answerCorrectness = isSolved ? (solveResult?.correct ?? null) : null
  const isAnswerLocked = isSolved || isViewSolved
  const correctAnswer = solveResult?.correctAnswers?.join(', ') ?? ''
  const displayedAnswer = isSolved && isShowingCorrectAnswers && correctAnswer
    ? correctAnswer
    : answer
  const displayedCorrectness = isShowingCorrectAnswers && correctAnswer
    ? true
    : answerCorrectness

  const renderedProblem = useMemo<ReactNode[]>(() => {
    if (!quiz.problem) return []

    return formatProblem(
      quiz.problem,
      `${quizId}-problem`,
      () => null,
      quiz.additionalImages ?? undefined,
    )
  }, [quiz.problem, quiz.additionalImages, quizId])

  return (
    <div className="space-y-6">
      {/* 문제 헤더 */}
      <QuizHeader
        quizId={quizId}
        typeLabel={t('types.explanation.label', '서술형')}
        title={quiz.title}
        difficultyLevel={difficultyLevel}
        variant="student"
        image={quiz.image}
        imageMobile={quiz.imageMobile}
        desmos={quiz.desmos}
        vars={quiz.vars}
      />

      {/* 문제 본문 — fill_blank과 동일한 파서로 텍스트/수식/추가 이미지를 렌더링 */}
      {renderedProblem.length > 0 && (
        <div
          data-quiz-problem-text
          className="text-2xl font-medium text-gray-900"
        >
          {renderedProblem}
        </div>
      )}

      {answerRevealOnly ? (
        isSolved && correctAnswer ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            role="status"
            className="w-full rounded-2xl border-2 border-transparent bg-lc-mint-200 p-4 text-2xl font-medium text-lc-mint-900"
          >
            <KatexRenderer text={correctAnswer} />
          </motion.div>
        ) : null
      ) : (
        /* 답변 입력 textarea */
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
        >
          {isAnswerLocked ? (
            <div
              data-testid="explanation-answer"
              aria-invalid={displayedCorrectness === false}
              className={cn(
                'min-h-[180px] w-full p-4 rounded-2xl border-2 text-2xl font-medium whitespace-pre-wrap break-words',
                displayedCorrectness === true && 'bg-lc-mint-200 text-lc-mint-900 border-transparent',
                displayedCorrectness === false && 'bg-lc-red-200 text-lc-red-900 border-transparent',
                displayedCorrectness === null && 'bg-lc-lightblue-100 text-gray-900 border-lc-lightblue-900',
              )}
            >
              <KatexRenderer text={displayedAnswer} />
            </div>
          ) : (
            <textarea
              value={displayedAnswer}
              onChange={(e) => setAnswer(e.target.value)}
              aria-invalid={displayedCorrectness === false}
              placeholder={t('types.explanation.placeholder', '답변을 입력하세요')}
              maxLength={500}
              rows={5}
              className={cn(
                'w-full p-4 rounded-2xl border-2 text-2xl font-medium text-gray-900 resize-y transition-all outline-hidden',
                'bg-white border-lc-gray-400 focus:border-lc-lightblue-900 focus:bg-lc-lightblue-200 [box-shadow:0px_4px_0px_rgba(0,0,0,0.1)] focus:[box-shadow:0px_4px_0px_rgba(85,195,255,0.3)]',
              )}
            />
          )}
          <div className="mt-1 text-right text-sm text-gray-400">
            {displayedAnswer.length}/500
          </div>
        </motion.div>
      )}

      {!answerRevealOnly && !hideResultFeedback && (
        <QuizResultFeedback
          isCorrect={answerCorrectness}
          show={isSolved && answerCorrectness === null}
          disableSound={disableSound}
          isViewSolved={isViewSolved}
        />
      )}

      {/* 정답 확인 버튼 */}
      {!answerRevealOnly && !isSolved && !isViewSolved && !hideSubmitButton && (
        <SubmitButton
          onClick={handleSubmit}
          disabled={!canSubmit}
          label={submitLabel}
        />
      )}

      {/* 풀이 결과 토글 (정답과 풀이 / 내가 쓴 답) */}
      {!answerRevealOnly && isSolved && !hideExplanation && (
        <QuizResultToggle
          value={isShowingCorrectAnswers ? 'correct-and-explanation' : 'my-answer'}
          onChange={(next: QuizResultToggleValue) => setIsShowingCorrectAnswers(next === 'correct-and-explanation')}
        />
      )}

      {/* 해설은 '정답과 풀이' 일 때만 노출 */}
      {answerRevealOnly && isSolved && !hideExplanation && !hideSolveDescription && (
        <QuizSolveDescription
          description={solveResult?.description ?? undefined}
          commentaryImages={quiz.commentaryImages}
          vars={quiz.vars}
          id={`${quizId}-desc`}
          actionVisible={false}
        />
      )}

      {!answerRevealOnly && isSolved && isShowingCorrectAnswers && !hideExplanation && !hideSolveDescription && (
        <QuizSolveDescription
          description={solveResult?.description ?? undefined}
          commentaryImages={quiz.commentaryImages}
          vars={quiz.vars}
          id={`${quizId}-desc`}
          actionVisible={false}
        />
      )}

    </div>
  )
}
